import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { PortalsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { useCanOpenStudio } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import { Option } from 'kingpin/atoms/ContextMenu';
import { buildStudioEdit } from 'navigation/appRoutes';
import EntityDetailsContext from 'screens/EntityDetailsShow/EntityDetailsContext';
import {
  ENTITY_APP_PARAM,
  ENTITY_PARAM,
} from 'screens/Studio/EntityPreview/useEntityPreview';
import { RETURN_TO_PARAM, RETURN_TO_STATE } from 'screens/Studio/returnTo';
import { BLOCK_PARAM } from 'screens/Studio/useOpenBlockParam';

// Edits a dashboard or report: in the Studio builder, wherever the UI has
// routes for it (see useCanOpenStudio). From an entity page, Studio previews
// it as that page's entity. From a gadget (blockId, its canvas card's id),
// Studio opens on its block. Back from Studio goes back to where it was
// opened (e.g. within a workspace or app). Elsewhere it's the classic editor
// (onClassicEdit), or nothing where there's none to offer (a block's menu).
const EditInStudioOption = ({
  contentType,
  id,
  blockId,
  onClassicEdit,
  icon,
}: {
  contentType: Studio.ContentType;
  id: string;
  blockId?: string;
  onClassicEdit?: () => void;
  // A gadget's menu shows its Edit with a pencil
  icon?: Kingpin.Icon;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const canOpenStudio = useCanOpenStudio();
  const { selectedPortal } = useContext(PortalsContext);
  const entityDetails = useContext(EntityDetailsContext);

  if (!canOpenStudio) {
    return onClassicEdit ? (
      <Option label="Edit" onClick={onClassicEdit} testId="edit-classic" />
    ) : null;
  }

  const params = new URLSearchParams();
  if (entityDetails && entityDetails.selectedEntity) {
    params.set(ENTITY_APP_PARAM, entityDetails.app.id);
    params.set(ENTITY_PARAM, entityDetails.selectedEntity.fieldValue);
  }
  if (blockId) {
    params.set(BLOCK_PARAM, blockId);
  }
  params.set(RETURN_TO_PARAM, `${location.pathname}${location.search}`);
  const search = params.toString();
  return (
    <Option
      label="Edit"
      icon={icon}
      onClick={() =>
        navigate(
          `${buildStudioEdit({
            contentType,
            id,
            portal: selectedPortal,
          })}${search ? `?${search}` : ''}`,
          { state: RETURN_TO_STATE },
        )
      }
      testId="edit-in-studio"
    />
  );
};

export default EditInStudioOption;
